import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

interface JsonViewerProps {
  /** Data to display */
  data: unknown;
  /** Maximum height before scrolling */
  maxHeight?: number | string;
}

/**
 * Simple JSON viewer with syntax highlighting
 */
const JsonViewer: React.FC<JsonViewerProps> = ({ data, maxHeight = 400 }) => {
  const formattedJson = useMemo(() => {
    if (data === undefined || data === null) {
      return 'null';
    }
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);

  if (!data) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        No data
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'grey.100',
        borderRadius: 1,
        p: 1.5,
        overflow: 'auto',
        maxHeight,
      }}
    >
      <Box
        component="pre"
        sx={{
          margin: 0,
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: 'grey.800',
        }}
      >
        {formattedJson}
      </Box>
    </Box>
  );
};

export default React.memo(JsonViewer);
